import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { bookExamples } from '../../data/bookExamples';

const API = process.env.REACT_APP_BACKEND_URL;
const BOOKS = [['4', 'Atomic Habits'], ['18', 'Deep Work'], ['7', 'The Psychology of Money'], ['5', 'Think and Grow Rich'], ['50', "Man's Search for Meaning"]];
const LANGS = [['bn', 'বাংলা'], ['hi', 'हिन्दी'], ['mr', 'मराठी'], ['gu', 'ગુજરાતી'], ['ta', 'தமிழ்'], ['te', 'తెలుగు'], ['kn', 'ಕನ್ನಡ'], ['ml', 'മലയാളം'], ['pa', 'ਪੰਜਾਬੀ']];
const ALL_IDS = bookExamples.map((b) => String(b.id));
const snapshot = (b) => ({ id: String(b.id), title: b.title, author: b.author || '', summary: b.summary || '', insights: b.insights || [], actionItems: b.actionItems || [], coreTheme: b.coreTheme || '' });

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

/* Admin: warm the shared translation caches ahead of users (runs in the background). */
export function PretranslateTool({ token }) {
  const [books, setBooks] = useState(BOOKS.map(([id]) => id));
  const [allBooks, setAllBooks] = useState(false);
  const [extra, setExtra] = useState('');
  const [langs, setLangs] = useState(['bn']);
  const [cap, setCap] = useState(50);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/api/admin/pretranslate/latest`, auth).then((r) => r.data.job && setJob(r.data.job)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!job || job.status !== 'running') return undefined;
    const t = setInterval(() => {
      axios.get(`${API}/api/admin/pretranslate/${job.id}`, auth).then((r) => setJob(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setError('');
    const picked = allBooks ? ALL_IDS : [...books, ...extra.split(',').map((s) => s.trim()).filter(Boolean)];
    const ids = [...new Set(picked)];
    const snapshots = Object.fromEntries(bookExamples.filter((b) => ids.includes(String(b.id))).map((b) => [String(b.id), snapshot(b)]));
    try {
      const r = await axios.post(`${API}/api/admin/pretranslate`, { book_ids: ids, languages: langs, snapshots, credit_cap: Number(cap) }, auth);
      setJob(r.data);
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not start pre-translation.');
    }
  };

  const count = allBooks ? ALL_IDS.length : books.length + extra.split(',').filter((s) => s.trim()).length;
  return (
    <div className="admin-diagnostic-card admin-test-email" data-testid="admin-pretranslate-card">
      <h3 className="admin-diagnostic-title">Pre-translate</h3>
      <p className="admin-diagnostic-help">Prepares ideas, practices (Kriya / Nimitta / Kala) and Phala/Vighna/Upaya suggestions ahead of time, so the first reader in a language doesn&apos;t wait. About 2 minutes and roughly 0.5 Universal Key credit per book and language. Runs in the background and stops cleanly at your credit cap or if the balance runs out; finished pieces are kept.</p>
      <div className="admin-waitlist-controls">
        <label className="admin-check"><input type="checkbox" checked={allBooks} onChange={() => setAllBooks(!allBooks)} data-testid="admin-pretranslate-all-books" /> <strong>All books ({ALL_IDS.length})</strong></label>
        {!allBooks && BOOKS.map(([id, title]) => (
          <label key={id} className="admin-check"><input type="checkbox" checked={books.includes(id)} onChange={() => setBooks(toggle(books, id))} data-testid={`admin-pretranslate-book-${id}`} /> {title}</label>
        ))}
        {!allBooks && <input className="admin-search-input" placeholder="Other book IDs, comma-separated" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="admin-pretranslate-extra" />}
      </div>
      <div className="admin-waitlist-controls" style={{ marginTop: 10 }}>
        {LANGS.map(([code, label]) => (
          <label key={code} className="admin-check"><input type="checkbox" checked={langs.includes(code)} onChange={() => setLangs(toggle(langs, code))} data-testid={`admin-pretranslate-lang-${code}`} /> {label}</label>
        ))}
      </div>
      <p className="admin-diagnostic-help" data-testid="admin-pretranslate-estimate">{count} book(s) × {langs.length} language(s) ≈ {(count * langs.length * 0.4).toFixed(1)}–{(count * langs.length * 0.7).toFixed(1)} credits, about {Math.ceil(count * langs.length * 2)} min.</p>
      <label className="admin-check">Stop at <input type="number" min="0.5" max="200" step="0.5" value={cap} onChange={(e) => setCap(e.target.value)} className="admin-search-input" style={{ width: 80 }} data-testid="admin-pretranslate-cap" /> credits (estimated, counted generously)</label>
      <button className="admin-action-btn admin-refresh-btn" style={{ marginTop: 12 }} onClick={start} disabled={job?.status === 'running' || !langs.length || !count} data-testid="admin-pretranslate-start">
        {job?.status === 'running' ? <><Loader2 className="admin-spinner" size={14} /> Running…</> : 'Start pre-translation'}
      </button>
      {error && <p className="admin-error" data-testid="admin-pretranslate-error">{error}</p>}
      {job && (
        <ul className="admin-diagnostic-meta" data-testid="admin-pretranslate-status">
          {job.credit_cap != null && <li data-testid="admin-pretranslate-credits"><strong>{(job.credits_used || 0).toFixed(2)} of {job.credit_cap} credits used</strong> · {job.calls || 0} AI calls</li>}
          {job.items.map((i) => <li key={`${i.book_id}-${i.lang}`}><strong>{i.book_title || i.book_id}</strong> · {i.lang} — {i.status}{i.seconds != null ? ` (${i.seconds}s` : ''}{i.credits != null ? `, ${i.credits.toFixed(2)} cr)` : i.seconds != null ? ')' : ''}</li>)}
          {job.status === 'running' && !job.items.length && <li>Starting…</li>}
          {job.status === 'stopped' && <li className="admin-error" data-testid="admin-pretranslate-stopped"><strong>Stopped.</strong> {job.stop_reason}</li>}
          {job.status === 'done' && <li><strong>All done.</strong> Finished {new Date(job.finished_at).toLocaleString()}</li>}
        </ul>
      )}
    </div>
  );
}
